import React, { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { USER_TYPES } from '@medflex/utils/constants';
import ContactActionMenu from '@medflex/components/stories/ContactActionMenu';
import { TRACKING_ACTIONS, trackEvent } from '@medflex/utils/Tracking';
import useLocationSearchParams from '@medflex/hooks/useLocationSearchParams';
import useModal from '../../hooks/useModal';
import usePermissions from '../../hooks/usePermissions';
import StaffActionMenu from './StaffActionMenu';
import useChatContacts from '../../hooks/useChatContacts';
import { MODALS } from '../../hooks/useIsModal';
import useExternalServices, { EXTERNAL_SERVICES } from '../../hooks/useExternalServices';

const AddressBookActionMenu = ({ contact, isOrgaContact, ...props }) => {
  const history = useHistory();
  const location = useLocation();
  const { open: openModal } = useModal();
  const dispatch = useDispatch();
  const params = new URLSearchParams(location.search);
  const { sort } = useLocationSearchParams();
  const { hasPatientAccess, hasPhysicianAccess } = usePermissions();
  const { toggleArchive } = useChatContacts();
  const [simplePrax] = useExternalServices([EXTERNAL_SERVICES.SIMPLEPRAX]);

  const pushQueryParams = state => history.push(`?${params.toString()}`, state);

  const handleActionClick = useCallback(
    modal => contactId => {
      openModal({ modal, contactId });
    },
    [openModal],
  );

  const handleStartQuestionnaire = () => {
    openModal({ modal: MODALS.NEW_QUESTIONNAIRE, userId: contact.user.id, tab: 'groups' });
  };

  const handleSharePatientContact = (event, contactUserId) => {
    trackEvent(event, { action: TRACKING_ACTIONS.OPEN });
    params.set('modal', 'sharePatientContact');
    params.set('filter', 'physicians');
    params.set('contactUserId', contactUserId);
    pushQueryParams();
  };

  const handleContactInfoClick = userId =>
    openModal({ modal: 'contact_info', userId, tab: 'groups' });

  if (contact) {
    switch (contact.type) {
      case 'session-chat':
        return null;
      case 'staff':
        return <StaffActionMenu contact={contact} {...props} />;
      default:
        return (
          <ContactActionMenu
            {...props}
            contactUserId={contact.user.id}
            contactId={contact.id}
            canCreateSubcontact={
              contact.type === USER_TYPES.PATIENT && !contact.mainContactId && !!contact.pairedAt
            }
            canChangeData={contact.type === USER_TYPES.PATIENT && !!contact.pairedAt}
            canResendInvitation={contact.type === USER_TYPES.PATIENT && !contact.pairedAt}
            canDeleteInvitation={contact.type === USER_TYPES.PATIENT && !contact.pairedAt}
            canSharePatientContact={
              contact.type === USER_TYPES.PATIENT &&
              !contact.mainContactId &&
              contact.pairedAt &&
              hasPatientAccess &&
              hasPhysicianAccess
            }
            canStartQuestionnaire={
              simplePrax?.active && contact.type === USER_TYPES.PATIENT && !!contact.pairedAt
            }
            canArchive={contact.pairedAt && !contact.isTeam && sort !== 'archive'}
            canDearchive={contact.pairedAt && sort === 'archive'}
            canDelete={contact.pairedAt && sort === 'archive' && !isOrgaContact}
            onChangeData={handleActionClick('patientData')}
            onCreateSubcontact={handleActionClick('createSubcontact')}
            onSharePatientContact={handleSharePatientContact}
            onResendInvitation={handleActionClick('resend_invitation')}
            onDeleteInvitation={handleActionClick('delete_invitation')}
            onViewContactInfos={handleContactInfoClick}
            onStartQuestionnaire={handleStartQuestionnaire}
            subtitle={
              contact.type === USER_TYPES.PATIENT &&
              !contact.mainContactId &&
              contact.pairedAt &&
              hasPatientAccess &&
              hasPhysicianAccess
            }
            onArchive={() => toggleArchive(contact)}
            onDearchive={() => toggleArchive(contact)}
            onDelete={handleActionClick('deleteContact')}
            onOpen={
              !contact.pairedAt && contact.type === USER_TYPES.PHYSICIAN
                ? () => dispatch.chatContacts.connectOrgaPhysician(contact.user.id)
                : () => {}
            }
          />
        );
    }
  }
  return null;
};

AddressBookActionMenu.propTypes = {
  contact: PropTypes.shape({
    isTeam: PropTypes.bool,
    id: PropTypes.string,
    enabled: PropTypes.bool,
    type: PropTypes.oneOf(['patient', 'session-chat', 'staff']),
    pairedAt: PropTypes.string,
    mainContactId: PropTypes.string,
    user: PropTypes.shape({
      id: PropTypes.string,
      subtitle: PropTypes.string,
      avatar: PropTypes.string,
      initials: PropTypes.string,
      fullNameWithTitle: PropTypes.string,
      organizationId: PropTypes.string,
    }),
  }),
  isOrgaContact: PropTypes.bool,
};

export default AddressBookActionMenu;
